import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const ServicesSection = () => {
  const [openService, setOpenService] = useState(null);

  const services = [
    {
      title: 'Business Setup & Advisory',
      description: 'Setting up a new venture involves far more than paperwork. We guide entrepreneurs and promoters through every stage — from selecting the right business structure to obtaining statutory registrations, establishing accounting systems, and ensuring ongoing compliance readiness.',
      items: [
        'Private Limited Company Registration',
        'Limited Liability Partnership (LLP) Registration',
        'One Person Company (OPC) Formation',
        'Partnership Firm Registration',
        'Proprietorship Firm Registration',
        'MSME / Udyam Registration',
        'GST Registration & Initial Compliance Setup',
        'Start-up India Registration',
        'Statutory Approvals & Licences'
      ]
    },
    {
      title: 'International Operations',
      subtitle: 'Global Expansion Services for Dubai & Singapore',
      description: 'We provide end-to-end advisory and execution support for businesses looking to expand internationally, with a strong focus on Dubai (UAE) and Singapore — two of the world\'s most business-friendly jurisdictions. Our services cover the complete lifecycle — from company incorporation and regulatory approvals to ongoing compliance, taxation, and business advisory.',
      groups: [
        {
          heading: 'Business Setup & Incorporation',
          items: [
            'Company formation in Dubai (Mainland, Free Zone, Offshore)',
            'Company incorporation in Singapore (Private Limited, LLP, Branch Office)',
            'Business activity selection and structuring',
            'Name reservation and approval',
            'Drafting of incorporation documents (MOA/AOA/Constitution)',
            'Assistance with regulatory approvals and licensing'
          ]
        },
        {
          heading: 'Regulatory Registrations & Licensing',
          items: [
            'Trade license issuance and renewal (UAE)',
            'ACRA registration and compliance (Singapore)',
            'UBO (Ultimate Beneficial Ownership) filings',
            'Economic Substance Regulation (ESR) compliance (UAE)',
            'Industry-specific approvals and registrations'
          ]
        },
        {
          heading: 'Visa & PRO Services (UAE)',
          items: [
            'Investor / Partner visa processing',
            'Employment visas and labour approvals',
            'Emirates ID assistance',
            'Family visa support',
            'End-to-end PRO services for government documentation'
          ]
        },
        {
          heading: 'Banking & Financial Setup',
          items: [
            'Corporate bank account opening assistance',
            'KYC documentation support',
            'Multi-currency account advisory',
            'Payment gateway integration',
            'Financial structuring for cross-border operations'
          ]
        },
        {
          heading: 'Accounting, Tax & Compliance',
          subGroups: [
            {
              label: 'Dubai (UAE)',
              items: [
                'VAT registration and return filing',
                'Corporate tax compliance and advisory',
                'Bookkeeping and MIS reporting',
                'Audit coordination'
              ]
            },
            {
              label: 'Singapore',
              items: [
                'GST registration and filing',
                'Corporate tax return filing',
                'Annual return filings with ACRA',
                'XBRL compliance'
              ]
            }
          ]
        },
        {
          heading: 'Corporate Secretarial Services',
          items: [
            'Annual compliance and statutory filings',
            'Maintenance of statutory registers',
            'Board resolutions and documentation',
            'Changes in directors / shareholders',
            'Share transfers and restructuring'
          ]
        },
        {
          heading: 'Office Setup & Substance Support',
          items: [
            'Virtual office and flexi-desk solutions',
            'Physical office setup assistance',
            'Ejari registration (Dubai tenancy)',
            'Support for Economic Substance compliance'
          ]
        },
        {
          heading: 'Intellectual Property & Legal Support',
          items: [
            'Trademark registration and protection',
            'Licensing and agreement support',
            'Legal documentation assistance'
          ]
        },
        {
          heading: 'Business Advisory & Structuring',
          items: [
            'Jurisdiction selection (Dubai vs Singapore vs India)',
            'Tax-efficient business structuring',
            'Holding company setup',
            'Cross-border transaction advisory',
            'Fund flow and repatriation planning'
          ]
        },
        {
          heading: 'Ongoing Compliance & Retainership',
          items: [
            'Monthly/quarterly compliance packages',
            'License and visa renewal tracking',
            'Virtual CFO services',
            'Compliance calendar management'
          ]
        },
        {
          heading: 'Exit & Closure Services',
          items: [
            'Company liquidation and deregistration',
            'License cancellation',
            'Visa cancellation',
            'Bank account closure assistance'
          ]
        }
      ],
      closingLine: 'We act as your single-point partner for global expansion — ensuring a seamless, compliant, and efficient setup and operation of your international business.'
    },
    {
      title: 'Audit & Assurance',
      description: 'Independent, objective assurance that your financial records reflect the true picture. We conduct the full spectrum of audits required under Indian statute and sector-specific regulations.',
      items: [
        'Under Companies Act, 2013: Financial Audit u/s 139 | Cost Audit u/s 148 | Secretarial Audit u/s 208 | Internal Audit u/s 138(1)',
        'Under Direct & Indirect Tax Laws: Tax Audit u/s 44AB (Income Tax Act) | GST Audit u/s 35(5) | Customs & Central Excise Audits | State VAT Audits',
        'Under Other Statutes: Concurrent Audit, Branch Audit & Stock Audit under Banking Regulation Act | Billing & Metering Audit under TRAI | Audit of Stock Brokers & Credit Rating Agencies (SEBI) | Forensic Audit | Financial Audit of Banks, Insurance Companies, LLPs, HUFs, Societies & Trusts'
      ]
    },
    {
      title: 'Taxation Services',
      description: 'Comprehensive direct and indirect tax services — compliance, planning, filing, and representation — for individuals, businesses, and NRIs.',
      items: [
        'Income Tax Return Filing (Individuals, Firms, Companies, Trusts)',
        'Corporate Tax Planning & Advisory',
        'TDS / TCS Compliance & Returns',
        'GST Registration, Returns & Reconciliation',
        'GST Audit & Annual Return (GSTR-9/9C)',
        'International Taxation & DTAA Advisory',
        'NRI Taxation & Repatriation',
        'Income Tax Assessments & Appeals',
        'Form 15CA / 15CB Certification',
        'Transfer Pricing Advisory'
      ]
    },
    {
      title: 'Corporate & Transaction Advisory',
      description: 'Strategic guidance for businesses navigating restructuring, mergers, acquisitions, and complex transactional decisions.',
      items: [
        'Mergers & Acquisitions Advisory',
        'Due Diligence (Financial & Tax)',
        'Business Valuation',
        'Corporate Restructuring',
        'FEMA & RBI Compliance',
        'Joint Venture Structuring',
        'Foreign Direct Investment (FDI) Advisory',
        'ROC Compliance & Secretarial Services',
        'MCA Annual Filings & Director KYC'
      ]
    },
    {
      title: 'Project Finance & Fund Raising',
      description: 'Supporting businesses in accessing growth capital through debt and equity routes, with preparation of all supporting documentation.',
      items: [
        'Project Report Preparation',
        'CMA Data & Financial Projections',
        'Bank Loan Documentation & Liaison',
        'Working Capital Assessment',
        'Private Equity & Venture Capital Advisory',
        'NBFC Compliance',
        'Startup Funding Advisory'
      ]
    },
    {
      title: 'Finance & Accounting',
      description: 'Reliable, accurate outsourced accounting and financial management to keep your operations running smoothly.',
      items: [
        'Bookkeeping & Accounts Maintenance',
        'MIS Reporting & Financial Dashboards',
        'Payroll Processing & Compliance',
        'Virtual CFO Services',
        'Accounts Payable / Receivable Management',
        'Financial Statement Preparation (Ind AS / IFRS)',
        'Cash Flow Management'
      ]
    },
    {
      title: 'Credit Risk & Due Diligence Services',
      description: 'A technology-driven field services platform supporting lending institutions — banks and NBFCs alike — across diverse domains and geographies throughout Rajasthan and Delhi NCR.',
      items: [
        'Personal Discussion (PDs)',
        'Liquid Income Profile (LIPs)',
        'Assessed Income Program (AIP)',
        'Risk Containment Unit (RCU)',
        'Credit Processing Agency (CPA)'
      ]
    }
  ];

  const toggleService = (index) => {
    setOpenService(openService === index ? null : index);
  };

  const renderServiceContent = (service) => {
    // International Operations — grouped content
    if (service.groups) {
      return (
        <div className="px-6 pb-8 pt-2">
          {service.subtitle && (
            <p className="text-[#C9A84C] font-playfair font-semibold text-lg mb-3">
              {service.subtitle}
            </p>
          )}
          <p className="text-gray-300 font-dmsans mb-6 leading-relaxed">
            {service.description}
          </p>

          <div className="space-y-6">
            {service.groups.map((group, gi) => (
              <div key={gi} className="border-l-2 border-[#C9A84C] border-opacity-30 pl-4">
                <h4 className="text-[#C9A84C] font-playfair font-bold text-base mb-3 flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#C9A84C] flex-shrink-0"></span>
                  {group.heading}
                </h4>

                {/* Flat items */}
                {group.items && (
                  <ul className="space-y-1.5">
                    {group.items.map((item, ii) => (
                      <li key={ii} className="flex items-start text-gray-300 font-dmsans text-sm">
                        <span className="text-[#C9A84C] mr-3 mt-1 flex-shrink-0">▪</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Sub-groups: Dubai / Singapore */}
                {group.subGroups && (
                  <div className="space-y-4">
                    {group.subGroups.map((sg, si) => (
                      <div key={si}>
                        <p className="text-gray-400 font-dmsans text-sm font-semibold mb-1.5 underline underline-offset-2">
                          {sg.label}
                        </p>
                        <ul className="space-y-1.5">
                          {sg.items.map((item, ii) => (
                            <li key={ii} className="flex items-start text-gray-300 font-dmsans text-sm">
                              <span className="text-[#C9A84C] mr-3 mt-1 flex-shrink-0">▪</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Closing line */}
          {service.closingLine && (
            <div className="mt-6 pt-5 border-t border-[#C9A84C] border-opacity-30">
              <p className="text-[#C9A84C] font-dmsans text-sm italic leading-relaxed">
                ✨ {service.closingLine}
              </p>
            </div>
          )}
        </div>
      );
    }

    // Standard flat list
    return (
      <div className="px-6 pb-6 pt-2">
        <p className="text-gray-300 font-dmsans mb-4 leading-relaxed">
          {service.description}
        </p>
        <ul className="space-y-2">
          {service.items.map((item, itemIndex) => (
            <li key={itemIndex} className="flex items-start text-gray-300 font-dmsans text-sm">
              <span className="text-[#C9A84C] mr-3 mt-1 flex-shrink-0">▪</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0D1B2A] via-[#1a2d42] to-[#0D1B2A]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(0deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px),
                           repeating-linear-gradient(90deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px)`
        }}></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-playfair font-bold text-[#C9A84C] mb-4">
            Our Services
          </h2>
          <div className="w-24 h-1 bg-[#C9A84C] mx-auto mb-6"></div>
          <p className="text-gray-300 font-dmsans max-w-3xl mx-auto">
            Comprehensive professional services tailored to your business needs.
          </p>
        </div>

        <div className="space-y-4">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-[#1a2d42] bg-opacity-50 rounded-lg shadow-md overflow-hidden border border-[#C9A84C] border-opacity-20 hover:border-[#C9A84C] transition-all duration-300"
            >
              <button
                onClick={() => toggleService(index)}
                className="w-full px-6 py-5 flex justify-between items-center text-left hover:bg-[#0D1B2A] hover:bg-opacity-50 transition-colors duration-300"
              >
                <div>
                  <h3 className="text-xl sm:text-2xl font-playfair font-bold text-[#C9A84C] mb-1">
                    {service.title}
                  </h3>
                  <p className="text-sm text-gray-400 font-dmsans">
                    {openService === index ? 'Click to collapse' : 'Click to expand'}
                  </p>
                </div>
                <div className="text-[#C9A84C]">
                  {openService === index ? <ChevronUp size={28} /> : <ChevronDown size={28} />}
                </div>
              </button>

              <div
                className={`transition-all duration-500 ease-in-out ${
                  openService === index ? 'max-h-[6000px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                {renderServiceContent(service)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
